import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, MapPin, LogIn, Map as MapIcon, Plus, Camera, Clock,
  ThumbsUp, Bell, ShieldCheck, ArrowRight,
} from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';

const STEPS = [
  {
    Icon: LogIn,
    title: 'Sign in with Google',
    body: 'One tap to join your neighborhood. The first time, you pick a username — your real name and photo always stay private. Others only ever see your username.',
  },
  {
    Icon: MapIcon,
    title: 'Explore the map',
    body: 'Pins show free curbside finds and yard sales near you. Zoom in and clusters break apart into individual items. Use the filters to show Free Items, Yard Sales, or everything.',
  },
  {
    Icon: Plus,
    title: 'Drop a pin',
    body: 'Got something to give away or a sale coming up? Tap "Drop a Pin", drag the marker to the exact spot, and add a title and description. Free items require a photo so neighbors know what to expect.',
  },
  {
    Icon: Camera,
    title: 'Add photos',
    body: 'Snap or upload up to a few photos. Clear pictures get items claimed faster. All images are automatically screened to keep the community safe.',
  },
  {
    Icon: Clock,
    title: 'Set a time window',
    body: 'Choose when your item or sale starts and ends. Pins automatically disappear when the time is up, so the map always shows what is actually available right now.',
  },
  {
    Icon: ThumbsUp,
    title: '“Is this still here?”',
    body: 'Neighbors can vote whether an item is still available. This keeps listings fresh and trustworthy — and tidies up the map for everyone.',
  },
  {
    Icon: Bell,
    title: 'Get nearby alerts',
    body: 'Turn on notifications in Account settings and set your alert location and radius. We will ping you the moment a free item or yard sale is posted near you.',
  },
  {
    Icon: ShieldCheck,
    title: 'Stay safe & kind',
    body: 'Be honest about what you list, remove pins once items are gone, and keep things respectful. Meet in safe, public-friendly spots and use your best judgment.',
  },
];

export default function TutorialPage() {
  const [site, setSite] = useState({ logo_text_left: 'Curb', logo_text_right: 'Alert' });
  const { user, login } = useAuth();

  useEffect(() => {
    api.get('/site-content').then(r => setSite(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-brand-paper" data-testid="tutorial-page">
      <div className="max-w-3xl mx-auto px-6 sm:px-10 py-10">
        <Link to="/" className="inline-flex items-center gap-2 text-brand-slate hover:text-brand-ink mb-8" data-testid="back-to-map">
          <ArrowLeft className="w-4 h-4" /> Back to the map
        </Link>

        <div className="flex items-center gap-2 mb-6">
          <MapPin className="w-5 h-5 text-brand" />
          <div className="font-display font-black text-2xl tracking-tight">
            <span className="text-brand-ink">{site.logo_text_left}</span>
            <span className="text-brand">{site.logo_text_right}</span>
          </div>
        </div>

        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight text-brand-ink leading-[1.05] mb-3">
          How CurbAlert works
        </h1>
        <p className="text-brand-slate text-base sm:text-lg mb-10 max-w-xl">
          Your neighborhood map for free curbside finds and yard sales. Here's everything you need to know to start sharing and grabbing treasures nearby.
        </p>

        <div className="space-y-4">
          {STEPS.map(({ Icon, title, body }, i) => (
            <div
              key={title}
              data-testid={`tutorial-step-${i + 1}`}
              className="glass-panel rounded-3xl p-5 sm:p-6 flex gap-4 sm:gap-5 items-start"
            >
              <div className="shrink-0 w-11 h-11 rounded-2xl bg-brand/10 flex items-center justify-center relative">
                <Icon className="w-5 h-5 text-brand" />
                <span className="absolute -top-2 -left-2 w-6 h-6 rounded-full bg-brand text-white text-xs font-bold flex items-center justify-center">
                  {i + 1}
                </span>
              </div>
              <div className="min-w-0">
                <h3 className="font-display font-bold text-lg text-brand-ink mb-1">{title}</h3>
                <p className="text-brand-slate text-sm leading-relaxed">{body}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 glass-panel rounded-3xl p-7 sm:p-8 text-center">
          <h2 className="font-display font-black text-2xl text-brand-ink mb-2">Ready to dive in?</h2>
          <p className="text-brand-slate text-sm mb-5">Find treasures nearby or share something with your neighbors.</p>
          {user ? (
            <Button asChild data-testid="tutorial-cta-map" className="rounded-full bg-brand hover:bg-brand-hover text-white px-7 py-6 text-base">
              <Link to="/">Open the map <ArrowRight className="w-4 h-4 ml-2" /></Link>
            </Button>
          ) : (
            <Button data-testid="tutorial-cta-signin" onClick={login} className="rounded-full bg-brand hover:bg-brand-hover text-white px-7 py-6 text-base">
              Sign in with Google <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
